import React, { useState, useEffect } from 'react';
import { Key, Plus, Pencil, Trash2, Save, X, KeyRound, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import apiService from '../services/api';

const SSHKeyManager = ({ onSelectKey }) => {
  const [keys, setKeys] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    authType: 'password',
    password: '',
    privateKey: ''
  });

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    try {
      const response = await apiService.getSSHKeys();
      if (response.success) {
        setKeys(response.keys);
      }
    } catch (error) {
      console.error('Failed to load SSH keys:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingKey) {
        await apiService.updateSSHKey(editingKey.id, formData);
      } else {
        await apiService.createSSHKey(formData);
      }
      loadKeys();
      resetForm();
    } catch (error) {
      alert('Failed to save SSH key: ' + error.message);
    }
  };

  const handleEdit = async (key) => {
    try {
      const response = await apiService.getSSHKey(key.id);
      if (response.success) {
        setEditingKey(response.key);
        setFormData({
          name: response.key.name,
          username: response.key.username,
          authType: response.key.privateKey ? 'key' : 'password',
          password: response.key.password || '',
          privateKey: response.key.privateKey || ''
        });
        setOpenDialog(true);
      }
    } catch (error) {
      alert('Failed to load key: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this SSH key?')) return;

    try {
      await apiService.deleteSSHKey(id);
      loadKeys();
    } catch (error) {
      alert('Failed to delete key: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      username: '',
      authType: 'password',
      password: '',
      privateKey: ''
    });
    setEditingKey(null);
    setOpenDialog(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold flex items-center gap-2">
          <Key className="h-4 w-4 text-primary" />
          Saved Keys
        </span>
        <Button size="sm" onClick={() => setOpenDialog(true)} className="h-7 text-xs">
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Key
        </Button>
      </div>

      {keys.length === 0 ? (
        <Alert className="py-3">
          <AlertDescription className="text-xs">
            No SSH keys saved. Add a key for quick deployment.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-2 max-h-[250px] overflow-y-auto">
          {keys.map(key => (
            <div
              key={key.id}
              className="flex items-center justify-between p-3 border border-border/50 rounded-lg hover:border-primary/50 transition-colors bg-muted/30"
            >
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <KeyRound className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium">{key.name}</p>
                  <p className="text-xs text-muted-foreground">@{key.username}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {onSelectKey && (
                  <Button size="sm" variant="secondary" onClick={() => onSelectKey(key)} className="h-7 text-xs">
                    Use
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => handleEdit(key)} className="h-7 w-7">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => handleDelete(key.id)} className="h-7 w-7">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {editingKey ? 'Edit SSH Key' : 'Add SSH Key'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Key Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Production Server"
                className="h-9 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Username</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="ec2-user"
                className="h-9 text-sm"
              />
            </div>

            <Tabs
              value={formData.authType}
              onValueChange={(v) => setFormData({ ...formData, authType: v })}
            >
              <TabsList className="h-8 grid w-full grid-cols-2 bg-muted/50">
                <TabsTrigger value="password" className="text-xs h-7 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  <Lock className="h-3.5 w-3.5 mr-1" />
                  Password
                </TabsTrigger>
                <TabsTrigger value="key" className="text-xs h-7 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  <KeyRound className="h-3.5 w-3.5 mr-1" />
                  SSH Key
                </TabsTrigger>
              </TabsList>
              <TabsContent value="password" className="mt-3">
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  className="h-9 text-sm"
                />
              </TabsContent>
              <TabsContent value="key" className="mt-3">
                <Textarea
                  value={formData.privateKey}
                  onChange={(e) => setFormData({ ...formData, privateKey: e.target.value })}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                  rows={5}
                  className="text-xs font-mono"
                />
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetForm} size="sm" className="h-8 text-xs">
              <X className="h-3.5 w-3.5 mr-1" />
              Cancel
            </Button>
            <Button onClick={handleSubmit} size="sm" className="h-8 text-xs">
              <Save className="h-3.5 w-3.5 mr-1" />
              {editingKey ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SSHKeyManager;
